import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import type { SxProps } from "@mui/material/styles";
import useTheme from "@mui/material/styles/useTheme";

import { useAppSelector } from "app/hooks";
import AssignedTagsList from "components/assigned-tags";
import PatientAlertChip from "components/patient-search/patient-alert-chip.component";
import CallPatientIcon from "components/suno-dialer/call-patient/call-patient-icon.component";
import { TagTypes } from "constants/tags";
import {
  selectCurrentPractice,
  selectNumberOfPracticeClinics
} from "features/auth/auth-slice";
import { useGetPatientAssignedTagsQuery } from "services/assigned-tags";
import type { Patient } from "services/types/patients.type";
import {
  formatDateOnlyValue,
  formatPhoneNumber,
  isDefaultPhoneNumber
} from "utils/common";

const MAX_PATIENT_TAGS_DISPLAY = 6;

type PatientSearchOptionType = {
  renderProps?: React.HTMLAttributes<HTMLLIElement>;
  option: Patient;
  allowAddNewPatient?: boolean;
  showPatientTags?: boolean;
  hideAvatar?: boolean;
  sx?: SxProps;
  showCheckbox?: boolean;
  isSelected?: boolean;
  showPatientAlertCount?: boolean;
  onCheckboxClick?: (patient: Patient) => void;
};

export default function PatientSearchOption({
  renderProps,
  option,
  allowAddNewPatient,
  showPatientTags,
  hideAvatar = false,
  sx,
  showCheckbox = false,
  isSelected = false,
  showPatientAlertCount = false,
  onCheckboxClick
}: PatientSearchOptionType) {
  const theme = useTheme();
  const currentPractice = useAppSelector(selectCurrentPractice);
  const showInactivePatients = Boolean(
    currentPractice?.show_deactivated_patients_in_search
  );
  const numberOfPracticeClinics = useAppSelector(selectNumberOfPracticeClinics);

  const skipAssignedTagsQuery =
    !showPatientTags || option?.assigned_tags !== undefined;

  const { data: assigned_tags } = useGetPatientAssignedTagsQuery(
    {
      object_id: option?.id
    },
    {
      skip: skipAssignedTagsQuery
    }
  );

  let title = [
    option.first_name,
    option.preferred_name ? `(${option.preferred_name})` : "",
    option.middle_name ? option.middle_name[0] : "",
    option.last_name
  ]
    .filter(Boolean)
    .join(" ");

  const isNewPatient = allowAddNewPatient && option.id === -1;
  const activeAlertCount = option.active_alert_count ?? 0;

  if (isNewPatient) {
    const fullName = [option.first_name, option.middle_name, option.last_name]
      .filter((namePart) => Boolean(namePart))
      .join(" ");
    title = `Add "${fullName || option.phone || option.email}" as new Patient`;
  }

  const handleCheckboxClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onCheckboxClick?.(option);
  };

  return (
    <ListItem
      {...renderProps}
      data-testid="patient-search-item"
      sx={{
        ...sx,
        "&.MuiAutocomplete-option.Mui-focused": {
          bgcolor: theme.palette.patientSearch.bgcolor
        },
        ...(isSelected && {
          bgcolor: theme.palette.action.selected
        })
      }}
    >
      {showCheckbox && (
        <Checkbox
          checked={isSelected}
          onClick={handleCheckboxClick}
          sx={{ mr: 1 }}
          size="small"
        />
      )}
      <ListItemText
        primary={
          <Stack direction="row" alignItems="center">
            <Box
              sx={{
                textDecoration:
                  (showInactivePatients || showCheckbox) &&
                  option &&
                  !isNewPatient &&
                  !option.is_active
                    ? "line-through"
                    : undefined
              }}
            >
              {title}
            </Box>
            {option.id !== -1 && (
              <Typography ml={1} pt={0.25} variant="caption" color={grey[600]}>
                (ID:&nbsp;{option.id})
              </Typography>
            )}

            {(showInactivePatients || showCheckbox) &&
              option &&
              !isNewPatient &&
              !option.is_active && (
                <Chip
                  label="Deactivated"
                  size="small"
                  sx={{
                    ml: 1
                  }}
                />
              )}
            {showPatientAlertCount && !isNewPatient && activeAlertCount > 0 && (
              <PatientAlertChip
                patientId={option.id}
                count={activeAlertCount}
              />
            )}
          </Stack>
        }
        primaryTypographyProps={{
          fontWeight: "fontWeightBold",
          component: "div"
        }}
        secondaryTypographyProps={{
          component: "div"
        }}
        secondary={
          <Stack spacing={0.5}>
            {Boolean(option.age_years || option.birthdate) && (
              <Stack
                direction="row"
                sx={{ color: grey[900], pb: 0.5 }}
                spacing={1}
              >
                {Boolean(option.birthdate) && (
                  <Typography component="span">
                    {formatDateOnlyValue(String(option.birthdate))}
                  </Typography>
                )}
                {Boolean(option.age_years) && (
                  <Typography component="span">
                    {option.age_years} years old
                  </Typography>
                )}
              </Stack>
            )}
            {Boolean(option.email) && (
              <Typography component="div" sx={{ color: grey[900], mb: 0.25 }}>
                <Typography component="span" sx={{ color: grey[800] }}>
                  Email:
                </Typography>{" "}
                {option.email}
              </Typography>
            )}
            {Boolean(option.phone) && !isDefaultPhoneNumber(option.phone) && (
              <Typography component="div" sx={{ color: grey[900], mb: 0.25 }}>
                <Typography component="span" sx={{ color: grey[800] }}>
                  Phone:
                </Typography>{" "}
                {formatPhoneNumber(option.phone, "parenthesis")}
                <CallPatientIcon
                  stopPropagation
                  phoneNumber={option.phone}
                  sx={{ ml: 0.25, my: -0.5 }}
                />
              </Typography>
            )}
            {Boolean(option.city || option.state) && (
              <Typography component="div" sx={{ color: grey[900], mb: 0.25 }}>
                <Typography component="span" sx={{ color: grey[800] }}>
                  Location:
                </Typography>{" "}
                {[option.city, option.state].filter(Boolean).join(", ")}
              </Typography>
            )}
            {option.secondary_phone && (
              <Typography component="div" sx={{ color: grey[900], mb: 0.25 }}>
                <Typography component="span" sx={{ color: grey[800] }}>
                  Secondary Phone:
                </Typography>{" "}
                {formatPhoneNumber(option.secondary_phone, "parenthesis")}
                <CallPatientIcon
                  stopPropagation
                  phoneNumber={option.secondary_phone}
                  sx={{ ml: 0.25, my: -0.5 }}
                />
              </Typography>
            )}

            {option.preferred_provider && (
              <Typography component="div" sx={{ color: grey[900], mb: 0.25 }}>
                <Typography component="span" sx={{ color: grey[800] }}>
                  Preferred Provider:
                </Typography>{" "}
                {option.preferred_provider.full_name}
              </Typography>
            )}

            {numberOfPracticeClinics > 1 && option.preferred_clinic && (
              <Typography component="div" sx={{ color: grey[900], mb: 0.25 }}>
                <Typography component="span" sx={{ color: grey[800] }}>
                  Preferred Clinic:
                </Typography>{" "}
                {`${
                  option.preferred_clinic?.region?.name
                    ? `${option.preferred_clinic?.region?.name}, `
                    : ""
                } ${option.preferred_clinic?.name ?? ""}`}
              </Typography>
            )}
            {showPatientTags && (
              <AssignedTagsList
                tagType={TagTypes.PATIENT}
                tags={option?.assigned_tags || assigned_tags}
                displayLimit={MAX_PATIENT_TAGS_DISPLAY}
                sx={{ flexDirection: "column" }}
              />
            )}
          </Stack>
        }
      />
      {!hideAvatar && (
        <ListItemAvatar sx={{ ml: 1 }}>
          <Avatar
            alt={title}
            src={option.avatar ?? ""}
            sx={{ width: 52, height: 52 }}
          />
        </ListItemAvatar>
      )}
    </ListItem>
  );
}
